import Game from './game';
import BaseObject, { BaseObjectParams } from './baseObject';
import Scene from './scene/scene';
import Element from './element/element';
import Sound from './audio/sound';

import { isBetween, avg } from '../utils/math';
import { CoordsType, getAngleBy2XY, getDistanceBy2XY, getXByAngle, getYByAngle } from '../utils/geometry';


interface Movement {
  x: number;
  y: number;
}

export interface CameraParams extends BaseObjectParams {
  contexts: Array<CanvasRenderingContext2D>;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  zoomRatio?: number;
  zoomRatioMin?: number;
  zoomRatioMax?: number;
  moveSpeed?: number; // units: pixels per second
  targetElements?: Array<Element>;
}

export default class Camera extends BaseObject implements CameraParams {
  id!: number;
  contexts: Array<CanvasRenderingContext2D> = [];
  x!: number;
  y!: number;
  width!: number;
  height!: number;
  zoomRatio = 1;
  zoomRatioMin = 0.1;
  zoomRatioMax = 4;

  moveSpeed = 2000; // units: pixels per second

  targetElements?: Array<Element>;

  movement: Movement;

  constructor(params: CameraParams) {
    super();
    this.applyParams(params);
    if (!this.contexts)
      this.contexts = Object.values(Game.instance.contexts);

    if (!this.width)
      this.width = Game.instance.canvasWidth;
    if (!this.height)
      this.height = Game.instance.canvasHeight;

    if (!this.x)
      this.x = this.width / 2;
    if (!this.y)
      this.y = this.height / 2;

    this.goToCoords(this.x, this.y);
    this.zoom(this.zoomRatio);

    this.movement = {
      x: 0,
      y: 0,
    };

    /* replace cursor in the camera */
    if (Game.instance.cursor) {
      Game.instance.cursor.x = this.x;
      Game.instance.cursor.y = this.y;
    }
  }

  get scene(): Scene {
    return Game.instance.scene;
  }

  get leftX(): number {
    return this.x - this.width / 2;
  }

  get topY(): number {
    return this.y - this.height / 2;
  }

  update(modifier: number): Camera {
    if (this.targetElements && this.targetElements.length > 0) // camera centered on specific element(s)
      this.focusElements(this.targetElements, modifier);
    else // user-controlled movement
    {
      const { x, y } = this.movement;
      const { moveSpeed } = this;
      this.move(
        x * moveSpeed * modifier / this.zoomRatio,
        y * moveSpeed * modifier / this.zoomRatio
      );
    }
    return this;
  }

  move(x = 0, y = 0): void {
    if (x === 0 && y === 0)
      return;

    const { x: moveX, y: moveY } = this.getMaxMoveValues(x, y); // keep the camera within the scene boundaries
    this.x += moveX;
    this.y += moveY;

    const cursor = Game.instance.cursor;
    if (cursor) {
      cursor.x += moveX;
      cursor.y += moveY;
    }

    this.translate(-moveX, -moveY);
  }

  goToCoords(x = 0, y = 0): void {
    const moveX = x - this.x;
    const moveY = y - this.y;
    this.move(moveX, moveY);
  }

  translate(x = 0, y = 0): void {
    Object.values(this.contexts)
      .forEach((ctx) => {
        ctx.translate(x, y);
      })
      ;

    /* trigger a fake mouseMove event on camera movement if RMB is kept pressed (with locked cursor) to give a movement order */
    const rtsControls = Game.instance.controlSchemes.rts;
    if (Game.instance.cursor.hasLock && rtsControls?.enabled && rtsControls?.rightMouseButtonPressed) {
      const fakeEvent = { ctrlKey: rtsControls.ctrlKeyPressed } as MouseEvent;
      const { x, y } = rtsControls.getPointerCoords(fakeEvent);
      rtsControls.handleMovementOrder(x, y, fakeEvent, true);
    }
  }

  scale(x = 1, y = 1): void {
    Object.values(this.contexts)
      .forEach((ctx) => {
        ctx.scale(x, y);
      })
      ;
  }

  zoom(ratio = 1): void {
    if (
      ratio === 1
      || !isBetween(this.zoomRatio * ratio, this.zoomRatioMin, this.zoomRatioMax) // not in min/max zoom range
    )
      return;

    const newWidth = this.width / ratio;
    const newHeight = this.height / ratio;
    if (newWidth > this.scene.width || newHeight > this.scene.height) return;

    /* get relative cursor's coords */
    const cursor = Game.instance.cursor;
    const cursorRelativeratioX = (cursor?.x - this.leftX) / this.width;
    const cursorRelativeRatioY = (cursor?.y - this.topY) / this.height;

    /* size scaling */
    this.scene.zoomRatio *= ratio;
    this.zoomRatio *= ratio;
    Sound.cameraModifier = this.zoomRatio;  // the farther the camera is, the weaker the sounds are
    this.scale(ratio, ratio); // canvas scale functions

    /* Scale Camera size according to the new zoom */
    this.width = newWidth;
    this.height = newHeight;

    /* translate CTXs to compensate zoom (centered zoom) */
    const toTranslateX = this.x / ratio - this.x;
    const toTranslateY = this.y / ratio - this.y;
    this.translate(toTranslateX, toTranslateY);

    /* Adjust camera position to keep it within the scene boundaries */
    if (this.x + this.width / 2 > this.scene.width)
      this.move(this.x + this.width / 2 - this.scene.width, 0);
    if (this.x - this.width / 2 < 0)
      this.move(Math.abs(this.x - this.width / 2), 0);
    if (this.y + this.height / 2 > this.scene.height)
      this.move(this.y + this.height / 2 - this.scene.height, 0);
    if (this.y - this.height / 2 < 0)
      this.move(0, Math.abs(this.y - this.height / 2));

    if (cursor) {
      /* Zoom towards the cursor */
      const newCursorX = this.leftX + cursorRelativeratioX * this.width;
      const newCursorY = this.topY + cursorRelativeRatioY * this.height;
      const moveAngle = getAngleBy2XY(
        ratio > 1
          ? { x1: this.x, x2: cursor.x, y1: this.y, y2: cursor.y }
          : { x1: cursor.x, x2: this.x, y1: cursor.y, y2: this.y }
      );
      const moveDistance = getDistanceBy2XY({ x1: cursor.x, x2: newCursorX, y1: cursor.y, y2: newCursorY });
      this.move(moveDistance * getXByAngle(moveAngle), moveDistance * getYByAngle(moveAngle));
    }

    /* Keep cursor at the same relative position */
    cursor.x = this.leftX + cursorRelativeratioX * this.width;
    cursor.y = this.topY + cursorRelativeRatioY * this.height;
  }

  resetZoom(): void {
    this.zoom(1 / this.zoomRatio);
  }

  getMaxMoveValues(x = 0, y = 0): CoordsType {
    /* Don't go out of boundaries */
    x = Math.min(
      Math.max(
        x,
        -(this.x - this.width / 2)
      ),
      this.scene.width - (this.x + this.width / 2)
    );
    y = Math.min(
      Math.max(
        y,
        -(this.y - this.height / 2)
      ),
      this.scene.height - (this.y + this.height / 2)
    );
    return { x, y };
  }

  focusElements(elements: Array<Element> = [], modifier?: number): void {
    const realElements = elements.filter((el: Element) => {
      return this.scene.elements[el.id];
    });

    if (realElements.length === 0)
      return;

    const avgX = avg(realElements.map(e => e.x));
    const avgY = avg(realElements.map(e => e.y));

    let moveX;
    let moveY;

    if (!modifier) {
      moveX = (avgX - this.x);
      moveY = (avgY - this.y);
    }
    else {
      const speedModifier = 10; // about 1/10s to reach the objective
      moveX = (avgX - this.x) * modifier * speedModifier;
      moveY = (avgY - this.y) * modifier * speedModifier;
    }
    this.move(moveX, moveY);
  }
}
